import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { EdgeworthDataService } from '../edgeworth/edgeworth-data.service';
import { OfficeFeedDataService } from '../officefeed/officefeed-data.service';
import { RecommendedDataService } from '../recommended/recommended-data.service';
import { CreateMenuService } from '../shared/create-menu/create-menu.service';
import { FileType } from '../shared/models/file-item.model';
import { OperationStream } from '../shared/models/operation-stream.model';
import { SectionState } from '../shared/models/section-state.enum';
import { MruConfig, MruConfigService } from '../shared/mru/mru-config.service';
import { MruData, MruDataService } from '../shared/mru/mru-data.service';
import { EventService } from '../shared/services/event.service';
import { OneDriveRootUrls } from '../shared/services/onedrive.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesConfig, CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UploadConfigService } from '../upload/upload-config.service';
import { OneDriveUploadService } from '../upload/upload.service';

@Component({
    selector: 'ohp-documents-component',
    templateUrl: './documents.component.html'
})
export class DocumentsComponent implements OnInit, OnDestroy {
    // Whether the mru is the first section displayed (controls top padding)
    @Input() public firstSection: boolean;

    @Input() public fileType: FileType;

    // Disables the header and new button
    @Input() public headless: boolean;

    @Input() public maxInitialDocs: number;

    @Input() public maxTotalDocs: number;

    @Input() public isSuggestedActions: boolean;

    @Output() public readonly mruStateChanged: EventEmitter<SectionState>;

    public title: string;
    public sectionLandmarkAriaLabel: string;
    public initialDocsAvailable: boolean;
    public recentMruStream: OperationStream<MruData>;
    public rootUrlTask: Promise<OneDriveRootUrls>;
    public capabilities: CapabilitiesConfig;
    public uploadEnabledPromise: Promise<boolean>;
    public uploadEnabled: boolean = false;
    public showZeroExperience: boolean;
    public createButtonEnabled: boolean;
    public hasRecommendedDocs: boolean;
    public numLoggableItems: number = 0;
    public mruOutputs = {
        stateChanged: value => this.onMruStateChange(value)
    };
    public edgeworthEnabled: boolean;
    public edgeworthReactEnabled: boolean;
    public quickAccessEnabled: boolean;
    public recommendedReact: boolean;
    public leftNavReact: boolean;
    public isConsumer: boolean;
    public spacesFolder: string;
    public isStartpage: boolean;
    public noGutter: boolean;

    private appFilter: Set<FileType>;
    private mruConfig: MruConfig;
    private recommendedSubscription: Subscription;
    private mruSubscription: Subscription;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly eventService: EventService,
        private readonly mruConfigService: MruConfigService,
        private readonly locService: LocService,
        private readonly mruDataService: MruDataService,
        private readonly officeFeedService: OfficeFeedDataService,
        private readonly createMenuService: CreateMenuService,
        private readonly recommendedService: RecommendedDataService,
        private readonly oneDriveUploadService: OneDriveUploadService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly edgeworthDataService: EdgeworthDataService,
        private readonly contextService: ContextService
    ) {
        this.mruStateChanged = new EventEmitter<SectionState>();
    }

    public ngOnInit() {
        this.capabilities = this.capabilitiesService.capabilities;
        this.mruConfig = this.mruConfigService.config;
        this.sectionLandmarkAriaLabel = this.locService.MruSectionTitleShort;
        this.title = this.capabilities.detailedConsumerHeadersEnabled ? this.locService.MruHeaderConsumer : this.sectionLandmarkAriaLabel;
        this.isStartpage = !!this.fileType;
        this.appFilter = this.fileType ? new Set([this.fileType]) : null;
        if (this.fileType && this.fileType === FileType.Stream) {
            this.appFilter.add(FileType.Video);
        }
        this.edgeworthEnabled = this.capabilities.edgeworthEnabled && this.fileType !== FileType.Forms && this.fileType !== FileType.OneNote && this.fileType !== FileType.Sway;
        this.edgeworthReactEnabled = this.capabilities.edgeworthReactEnabled;
        this.quickAccessEnabled = this.capabilities.quickAccessV1Enabled && !this.isStartpage;
        this.recommendedReact = this.capabilities.recommendedReact;
        this.leftNavReact = this.capabilities.leftNavReact;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.noGutter = this.capabilities.fluentRefresh || (this.quickAccessEnabled && !this.isConsumer);
        this.spacesFolder = (this.capabilities.spacesEnabled && this.fileType === FileType.Spaces) ? this.locService.SpacesFolderName : '';

        this.initializeUpload();
        this.initializeCreateButton();
        this.initializeRecommended();

        // If edgeworth is turned off, don't initialize MRU
        if (!this.edgeworthEnabled) {
            this.initializeMru();
        } else {
            this.contextService.isEdgeworth = true;
            if (!this.edgeworthReactEnabled) {
                this.edgeworthDataService.init(this.appFilter);
            }
        }
    }

    public ngOnDestroy() {
        if (this.recommendedSubscription) {
            this.recommendedSubscription.unsubscribe();
        }

        if (this.mruSubscription) {
            this.mruSubscription.unsubscribe();
        }
    }

    public onMruStateChange(mruSectionState: SectionState) {
        this.mruStateChanged.emit(mruSectionState);
    }

    private initializeUpload(): void {
        if (this.capabilities.oneDriveUploadEnabled && this.uploadConfigService.config && this.fileType !== FileType.Sway && this.fileType !== FileType.OneNote && this.fileType !== FileType.Forms) {
            this.oneDriveUploadService.uploadEnabledPromise.then(uploadEnabled => this.uploadEnabled = uploadEnabled);
        }
    }

    private initializeCreateButton(): void {
        this.createMenuService.createButtonEnabledPromise
            .then(createButtonEnabled => this.createButtonEnabled = createButtonEnabled);
    }

    private initializeRecommended(): void {
        const isOfficeFeed = (this.mruConfig.officeFeedControlEnabled || this.mruConfig.officeFeedFlightEnabled) && !this.fileType;

        const recommendedEnabled = this.mruConfig.recommendedEnabled && this.fileType !== FileType.Sway && this.fileType !== FileType.OneNote && this.fileType !== FileType.Forms;

        if (recommendedEnabled) {
            const mruDataStream = (isOfficeFeed ? this.officeFeedService.getOfficeFeed() : this.recommendedService.getRecommendedDocs(this.appFilter));
            this.recommendedSubscription = mruDataStream.results
                .subscribe(data => {
                    this.eventService.broadcastEvent({
                        name: AngularEvents.recommendedDataReady,
                        args: [data.items.length]
                    });
                    this.hasRecommendedDocs = data.items.length > 0;
                });
        }
    }

    private initializeMru(): void {
        this.recentMruStream = this.mruDataService.getMruDocuments(this.appFilter);
        this.mruSubscription = this.recentMruStream.results.subscribe(mruData => {
            if (mruData.items.length) {
                this.initialDocsAvailable = true;
                this.numLoggableItems = mruData.items.length;
            } else {
                Promise.all([this.createMenuService.createButtonEnabledPromise, this.oneDriveUploadService.uploadEnabledPromise])
                    .then(([createButtonEnabled, uploadEnabled]) =>
                        this.showZeroExperience = this.fileType === FileType.Forms || this.mruConfig.showZeroExperience && (createButtonEnabled || uploadEnabled || this.clientStartupConfigService.config.authType === AuthType.OrgId));
            }
            this.eventService.broadcastEvent({
                name: AngularEvents.mruDataReady,
                args: [mruData.items.length]
            });
        });

        // Broadcast mruFailed event for MSA to start Dime warming up
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            this.recentMruStream.initialCompletion.then(error => {
                if (error && error.cacheError && error.networkError) {
                    this.eventService.broadcastEvent({
                        name: AngularEvents.mruFailed,
                        args: []
                    });
                }
            });
        }
    }
}
